import { observer } from 'mobx-react-lite';
import { PropsWithChildren, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router-dom';
import { BasePage, BasePageProps, PageType } from 'src/components/BasePage';
import { Button } from 'src/components/Button/Button';
import { Form } from 'src/components/Form/Form';
import { ConstraintViolationError } from 'src/utils/ErrorDataHelper';
import { FormHelper } from 'src/utils/FormHelper';
import { BasicStore } from 'src/utils/mobx/BasicStore/BasicStore';
import styled from 'styled-components';

type EditFormPage = PropsWithChildren<
    Pick<
        BasePageProps,
        'isLoading' | 'showOnlyLoading' | 'title' | 'headerTopPanel'
    > & {
        pageName: string;
        store: BasicStore<any>;
        saveText?: React.ReactNode;
        basePageProps?: Partial<BasePageProps>;
        onCancel?: () => void;
        handleFinish?: (values: any) => Promise<void>;
    }
>;

export const EditFormPage = observer(
    ({
        children,
        store,
        pageName,
        saveText,
        title,
        isLoading,
        showOnlyLoading,
        headerTopPanel,
        basePageProps,
        onCancel,
        handleFinish,
    }: EditFormPage) => {
        const { t } = useTranslation();
        const {
            params: { id },
        } = useRouteMatch<{ id: string }>();

        useEffect(() => {
            store.loadItem(id);

            return () => {
                store.itemLoader.reset();
            };
        }, []);

        const fields = FormHelper.getFieldDataItems(
            store.updateItemLoader.errorData as ConstraintViolationError,
        );

        return (
            <BasePage
                pageName={`${pageName}-${id}`}
                type={PageType.BASE}
                title={title}
                isLoading={isLoading}
                showOnlyLoading={showOnlyLoading}
                headerTopPanel={headerTopPanel}
                {...basePageProps}
            >
                <Form
                    scrollToFirstError={{ block: 'center', behavior: 'smooth' }}
                    initialValues={store.currentItem}
                    fields={fields}
                    onFinish={handleFinish}
                >
                    {children}
                    <ButtonsContainer>
                        <Button onClick={onCancel}>
                            {t('App.Components.EditFormPage.Cancel')}
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={store.createItemLoader.isLoading}
                        >
                            {saveText ||
                                t('App.Components.EditFormPage.Update')}
                        </Button>
                    </ButtonsContainer>
                </Form>
            </BasePage>
        );
    },
);

const ButtonsContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;

    & > button:not(:last-child) {
        margin-right: 24px;
    }
`;
